import { Stack } from '@mantine/core';
import {
  AutomationStep,
  FreshContextRetrySchedule,
  RetryDataOutput,
  RetryStep,
  RetryStepConfig,
  RetryStepMode,
} from '@/types/automationSteps.ts';
import StepCommonFields from '@/pages/Editor/components/SharedFields/StepCommonFields.tsx';
import StepAdvancedConfigFields from '@/pages/Editor/components/SharedFields/StepAdvancedConfigFields.tsx';
import StyledNumberInput from '@/components/StyledInputFields/StyledNumberInput.tsx';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import StyledInputLabel from '@/components/StyledInputFields/StyledInputLabel.tsx';
import RetryDataOutputFields from '@/pages/Editor/components/StepSidebar/StepForms/RetryStep/RetryDataOutputFields.tsx';
import StepFieldsAddButton from '@/pages/Editor/components/StepFieldsAddButton.tsx';
import { generateUid } from '@/utils/misc.ts';
import { DEFAULT_DYNAMIC_DATA_INPUT } from '@/utils/constants.ts';

type Props = {
  step: RetryStep;
};

type Fields = {
  max_retry_count: number;
  retry_step_config: RetryStepConfig | null;
  data_outputs: RetryDataOutput[];
  children: AutomationStep[];
};

const freshContextScheduleOptions = [
  { value: FreshContextRetrySchedule.Immediate, label: 'Retry immediately' },
  { value: FreshContextRetrySchedule.PlatformBackoff, label: 'Use platform backoff' },
];

const defaultClassificationConfig = (classificationStepId: string): RetryStepConfig => ({
  mode: RetryStepMode.RetryOnClassificationSignal,
  classification_step_id: classificationStepId,
  fresh_context_retry_schedule: FreshContextRetrySchedule.Immediate,
  stop_on_ambiguous_outcome: true,
  retry_budget_exhausted_action: 'stop_with_errors',
  retry_budget_exhausted_policies: [],
});

export function RetryForm({ step }: Props) {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<Fields>({ step, syncWithAPI: true });
  const enabledChildren = fields.children.filter((child) => !child.is_disabled);
  const retryModeOptions = [
    { value: RetryStepMode.RetryOnChildError, label: 'Retry when a child step fails' },
    {
      value: RetryStepMode.RetryOnClassificationSignal,
      label: 'Retry when a classifier permits it',
      disabled: enabledChildren.length === 0,
    },
  ];
  const classificationConfig =
    fields.retry_step_config?.mode === RetryStepMode.RetryOnClassificationSignal
      ? fields.retry_step_config
      : null;

  const updateRetryMode = (mode: RetryStepMode) => {
    if (mode === RetryStepMode.RetryOnChildError) {
      onChangeNoDelay({
        retry_step_config: {
          mode,
          classification_step_id: null,
          fresh_context_retry_schedule: FreshContextRetrySchedule.Immediate,
          stop_on_ambiguous_outcome: true,
          retry_budget_exhausted_action: 'stop_with_errors',
          retry_budget_exhausted_policies: [],
        },
      });
      return;
    }

    const firstEnabledChild = enabledChildren[0];
    if (firstEnabledChild) {
      onChangeNoDelay({ retry_step_config: defaultClassificationConfig(firstEnabledChild.step_id) });
    }
  };

  const updateClassificationConfig = (updates: Partial<RetryStepConfig>) => {
    if (classificationConfig) {
      onChangeNoDelay({ retry_step_config: { ...classificationConfig, ...updates } });
    }
  };

  return (
    <Stack gap="md">
      <StepCommonFields step={step} nameLabel="Step Name" />
      <span>
        <StyledInputLabel>Outputs</StyledInputLabel>
        <Stack>
          {fields.data_outputs.map((dataOutput, idx) => (
            <RetryDataOutputFields
              key={dataOutput.output_id}
              dataOutput={dataOutput}
              onChangeNoDelay={(updatedOutput: RetryDataOutput) => {
                const updatedOutputs = fields.data_outputs.map((output) =>
                  output.output_id === updatedOutput.output_id ? updatedOutput : output,
                );
                onChangeNoDelay({ data_outputs: updatedOutputs });
              }}
              onChangeDelay={(updatedOutput: RetryDataOutput) => {
                const updatedOutputs = fields.data_outputs.map((output) =>
                  output.output_id === updatedOutput.output_id ? updatedOutput : output,
                );
                onChange({ data_outputs: updatedOutputs });
              }}
              onDelete={() => {
                const updatedOutputs = fields.data_outputs.filter(
                  (output) => output.output_id !== dataOutput.output_id,
                );
                onChangeNoDelay({ data_outputs: updatedOutputs });
              }}
              step={step}
              outputIndex={idx}
            />
          ))}

          <StepFieldsAddButton
            stepId={step.step_id}
            elementCount={fields.data_outputs.length}
            onClick={() => {
              const newOutput: RetryDataOutput = {
                output_id: generateUid(),
                name: '',
                child_data_output: DEFAULT_DYNAMIC_DATA_INPUT,
              };
              onChangeNoDelay({ data_outputs: [...fields.data_outputs, newOutput] });
            }}
          />
        </Stack>
      </span>
      <StyledNumberInput
        label="Retry attempts"
        description="Retries this step's child scope when a child step fails before an unproven external-effect boundary. Related child steps may receive failure guidance on later attempts."
        min={0}
        max={5}
        clampBehavior="strict"
        value={fields.max_retry_count}
        onChange={(value) => onChange({ max_retry_count: typeof value === 'number' ? value : 0 })}
      />
      <StyledSelect
        label="Retry decision"
        description="Direct retry stops after an external write, unknown-effect action, or started sub-automation. Use a classifier when another attempt needs a safety decision."
        data={retryModeOptions}
        value={classificationConfig ? RetryStepMode.RetryOnClassificationSignal : RetryStepMode.RetryOnChildError}
        onChange={(value) => value && updateRetryMode(value as RetryStepMode)}
        allowDeselect={false}
        clearable={false}
      />
      {classificationConfig && (
        <>
          <StyledSelect
            label="Retry classifier"
            description="This child step emits the typed retry decision for the current attempt."
            data={enabledChildren.map((child: AutomationStep) => ({
              value: child.step_id,
              label: child.name || child.step_id,
            }))}
            value={classificationConfig.classification_step_id}
            onChange={(value) => value && updateClassificationConfig({ classification_step_id: value })}
            allowDeselect={false}
            clearable={false}
            disabled={enabledChildren.length === 0}
          />
          <StyledSelect
            label="Fresh-session retry timing"
            description="After a classifier safely requests a fresh session, retry immediately or let the platform apply backoff."
            data={freshContextScheduleOptions}
            value={classificationConfig.fresh_context_retry_schedule}
            onChange={(value) =>
              value &&
              updateClassificationConfig({
                fresh_context_retry_schedule: value as FreshContextRetrySchedule,
              })
            }
            allowDeselect={false}
            clearable={false}
          />
        </>
      )}
      <StepAdvancedConfigFields step={step} />
    </Stack>
  );
}
